import React, { Fragment } from 'react'
import Apps from '../../../api/apps'
import Table from '../../shared/table'
import {
  PageHeading,
  Heading,
  Text,
  List,
  Code,
  CodeBlock,
  Link,
} from '../../shared/typography'
import { StackView, Alert } from '@planningcenter/tapestry-react'

const Token = () => {
  return (
    <Fragment>
      <Heading anchored id="personal-access-token" size={1}>
        Personal Access Token
      </Heading>

      <Text>
        A Personal Access Token (PAT) allows you to use the API from your own
        account. This is useful if you are writing a script that runs on your
        own computer or a server you control. These tokens cannot be used in
        client-side requests from a web page.
      </Text>

      <Text>
        You may not use a Personal Access Token if you are integrating with
        multiple churches&mdash;instead use OAuth (see below).
      </Text>

      <Alert type="warning" marginBottom={2}>
        <Text size={4}>
          <strong>Important:</strong> Never share your Personal Access Token
          with another user or company. It is the equivalent of giving someone
          your username and password.
        </Text>
      </Alert>

      <List ordered>
        <List.Item>
          <Text>
            Visit{' '}
            <Link
              to="https://api.planningcenteronline.com/oauth/applications"
              external
            >
              your developer account
            </Link>
            .
          </Text>
        </List.Item>
        <List.Item>
          <Text>Create a new "Personal Access Token"</Text>
        </List.Item>
        <List.Item>
          <Text>
            Pass your token and secret in every request using HTTP Basic Auth.
          </Text>
        </List.Item>
      </List>

      <Text>
        You can pass your personal access token with <Code>curl</Code> like
        this:
      </Text>

      <CodeBlock language="bash">
        {
          'curl -u app_id:secret https://api.planningcenteronline.com/people/v2/people'
        }
      </CodeBlock>
    </Fragment>
  )
}

const ScopesTable = () => {
  const columns = [
    {
      header: 'Product',
      cell: 'name',
      css: { flex: '1 1 0' },
    },
    {
      header: 'Scope',
      cell: row => <Code>{row.scope}</Code>,
      css: { flex: '1 1 0' },
    },
  ]

  const data = Apps.filter(({ scope }) => scope).map(({ name, scope }) => ({
    name,
    scope,
  }))

  return <Table columns={columns} data={data} />
}

const OAuth = () => {
  return (
    <Fragment>
      <Heading anchored id="oauth-2-0" size={1}>
        OAuth 2.0
      </Heading>

      <Text>
        If you are distributing your app to multiple churches, you should use{' '}
        <Link to="https://en.wikipedia.org/wiki/OAuth" external>
          OAuth
        </Link>{' '}
        version 2.0.
      </Text>

      <Text>
        First, your company needs to have a Planning Center account, which
        represents your organization. You can{' '}
        <Link href="https://accounts.planningcenteronline.com/sign_up" external>
          create one here
        </Link>
        . This feels a little funny at first, because it is like you are signing
        up as a church for Planning Center. But having a Planning Center account
        with a login gives you a place to manage your OAuth application. The
        name you give your Planning Center account (the "church" name) can just
        be your company name.
      </Text>

      <Text>
        Once you are logged into your Planning Center account, visit{' '}
        <Link to="https://api.planningcenteronline.com" external>
          api.planningcenteronline.com
        </Link>{' '}
        to create an OAuth application. This client ID and secret will only be
        created once, and will be reused for every church that signs up for your
        integration.
      </Text>

      <Alert type="warning" marginBottom={2}>
        <Text size={4}>
          <strong>Important:</strong> Only create one OAuth application. When
          using OAuth, churches do <strong>not</strong> need to create their own
          OAuth application. You can integrate with multiple churches using your
          single OAuth application that you control.
        </Text>
      </Alert>

      <Alert stroke="grey-6" backgroundColor="grey-4" marginBottom={2}>
        <Text size={4}>
          Starting March 22, 2023, only Organization Administrators can create
          OAuth applications, and all new applications are managed by all
          Organization Administrators in your organization. If you are part of a
          church you should consider{' '}
          <Link to="https://www.planningcenter.com/pricing">
            creating a new free organization
          </Link>{' '}
          to hold your application if the application doesn't belong to that
          church.
        </Text>
      </Alert>

      <Text>
        Make sure your app conforms to the{' '}
        <Link to="http://oauth.net/2/" external>
          OAuth 2.0
        </Link>{' '}
        specification. Generally, to authenticate a user, follow these steps:
      </Text>

      <List ordered>
        <List.Item>
          <Text>
            Redirect the user’s browser to{' '}
            <Code>
              https://api.planningcenteronline.com/oauth/authorize?client_id=CLIENT_ID&redirect_uri=https://example.com/auth/complete&response_type=code&scope=people
            </Code>{' '}
            (replace <Code>CLIENT_ID</Code> and{' '}
            <Code>https://example.com/auth/complete</Code> with your actual
            redirect URI).
          </Text>
          <Text>
            <em>If you need different scopes, replace <Code>scope=people</Code>{' '}
            appropriately (see “Application Scopes” section below).</em>
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            Planning Center will redirect the user’s browser back to the given
            redirect URI with a code param.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            Send a POST request in the background to{' '}
            <Code>https://api.planningcenteronline.com/oauth/token</Code> with
            the following params:{' '}
          </Text>
          <CodeBlock language="bash">
            {
              '{"grant_type": "authorization_code", "code": "CODE_FROM_STEP_2", "client_id": "CLIENT_ID", "client_secret": "CLIENT_SECRET", "redirect_uri": "https://example.com/auth/complete"}'
            }
          </CodeBlock>
          <Text>
            (replace <Code>CLIENT_ID</Code>, <Code>CLIENT_SECRET</Code>,{' '}
            <Code>CODE_FROM_STEP_2</Code>, and the redirect URI appropriately).
          </Text>

          <CodeBlock language="bash">
            {`curl -X POST https://api.planningcenteronline.com/oauth/token \\
              -F grant_type=authorization_code \\
              -F code=1234567890 \\
              -F client_id=2345678901 \\
              -F client_secret=3456789012 \\
              -F redirect_uri=https://example.com/auth/complete`}
          </CodeBlock>
        </List.Item>
        <List.Item>
          <Text>
            The response you get back will contain the access token and other
            information.
          </Text>

          <CodeBlock language="json">
            {JSON.stringify(
              {
                access_token:
                  '1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef',
                token_type: 'bearer',
                expires_in: 7200,
                refresh_token:
                  '1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef',
                scope: 'people',
                created_at: 1469553476,
              },
              null,
              2
            )}
          </CodeBlock>
        </List.Item>
        <List.Item>
          <Text>
            Use the access token for all API requests by passing it in the
            <Code>Authorization</Code> header, using the <Code>Bearer</Code>{' '}
            authentication scheme.
          </Text>
          <CodeBlock language="bash">
            {
              "curl -H 'Authorization: Bearer 1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef' https://api.planningcenteronline.com/people/v2/people"
            }
          </CodeBlock>
        </List.Item>
      </List>
      <Text>
        OAuth access tokens expire after 2 hours from the time they are issued,
        but we also provide a{' '}
        <Link to="https://tools.ietf.org/html/rfc6749#section-1.5" external>
          refresh token
        </Link>{' '}
        you can use to get a new access token at any time, even after the access
        token expires, without forcing the user to re-authorize.
      </Text>
      <Text>
        We will honor refresh tokens for up to 90 days after the date its
        associated access token was issued. This means that, if your app does
        not issue a refresh for 90 days, then your user will need to
        re-authorize.
      </Text>

      <Heading anchored id="scopes">
        Application Scopes
      </Heading>

      <Text>
        Each Planning Center product is a distinct{' '}
        <Link to="http://tools.ietf.org/html/rfc6749#section-3.3" external>
          OAuth scope
        </Link>
        .
      </Text>

      <ScopesTable />

      <Text>
        <em>
          Note that accessing Webhooks endpoints do not require an OAuth scope.
        </em>
      </Text>

      <Text>
        Authorization URL is{' '}
        <Link
          to="https://api.planningcenteronline.com/oauth/authorize"
          external
        >
          https://api.planningcenteronline.com/oauth/authorize
        </Link>{' '}
        Token URL is{' '}
        <Link to="https://api.planningcenteronline.com/oauth/token" external>
          https://api.planningcenteronline.com/oauth/token
        </Link>{' '}
        When authorizing, you can request scopes using the <Code>scope</Code>{' '}
        parameter. The value should be a space-separated list of scopes. The
        value for <Code>response_type</Code> should be <Code>code</Code>.
      </Text>

      <Heading anchored id="refreshing-a-token">
        Refreshing a token
      </Heading>

      <Text>
        You can refresh an expired Oauth token by using the{' '}
        <Code>refresh_token</Code> returned when you generated the token.
      </Text>

      <CodeBlock language="bash">
        {
          'curl -X "POST" "https://api.planningcenteronline.com/oauth/token" \\\n  -H \'Content-Type: application/json\' \\\n  -d $\'{\n    "client_id": "<your application client id>",\n    "client_secret": "<your application secret>",\n    "refresh_token": "<your access token refresh token>",\n    "grant_type": "refresh_token"\n  }\''
        }
      </CodeBlock>
      <Text>The response body will look like this:</Text>
      <CodeBlock language="json">
        {JSON.stringify(
          {
            access_token: '<new access token>',
            token_type: 'bearer',
            expires_in: 7200,
            refresh_token: '<new refresh token>',
            created_at: 1540325919,
          },
          null,
          2
        )}
      </CodeBlock>

      <Heading anchored id="current-user">
        Current User
      </Heading>

      <Text>
        To get information about the current user, you can use the{' '}
        <Code>/me</Code> endpoint.
      </Text>

      <CodeBlock language="bash">
        {
          "curl -H 'Authorization: Bearer ...' https://api.planningcenteronline.com/people/v2/me"
        }
      </CodeBlock>

      <Heading anchored id="oidc" size={1}>
        OpenID Connect (OIDC)
      </Heading>

      <Text>
        <Link to="https://openid.net/developers/how-connect-works/" external>OpenID Connect</Link> is an authentication layer on top of OAuth 2.0 that allows users of your applications to authenticate using their Planning Center account.
      </Text>

      <Text>
        The OIDC flow is very similar to the typical OAuth 2.0 flow, but
        requires requesting an additional <Code>openid</Code> scope. In fact,
        the <Code>openid</Code> scope can be requested alongside other
        application scopes (see the previous "Application Scopes" section).
      </Text>

      <Text>
        When requesting the <Code>openid</Code> scope, you'll receive an{' '}
        <Code>id_token</Code> (a JWT) in addition to the standard OAuth 2.0{' '}
        <Code>access_token</Code>.{' '}
        <Link
          to="https://openid.net/specs/openid-connect-core-1_0.html#IDToken"
          external
        >
          ID Tokens
        </Link>{' '}
        contain identity claim assertions for the currently logged in user.
      </Text>

      <Text>
        ID tokens will expire after 1 hour from the time they are issued.
      </Text>

      <Heading anchored id="oidc-discovery">
        OIDC Discovery
      </Heading>

      <Text>
        Planning Center supports OIDC discovery through the standard OpenID configuration endpoint:
      </Text>

      <CodeBlock language="bash">
        {'https://api.planningcenteronline.com/.well-known/openid-configuration'}
      </CodeBlock>

      <Text>
        This endpoint provides all the necessary configuration information (including supported scopes, token endpoints, and token signing information)
        and simplifies the configuration process for OIDC clients that support server discovery.
      </Text>

      <Heading anchored id="oidc-parameters">
        Additional OIDC Request Parameters
      </Heading>

      <Text>
        When initiating the OIDC authorization flow, the following request parameters are optional but strongly recommended,
        and should be used in addition to the standard OAuth flow request parameters:
      </Text>

      <List>
        <List.Item>
          <Text>
            <Code>state</Code> - A unique, unguessable string
            used to prevent CSRF attacks. Your application should verify this value matches the originally sent value when
            the user is redirected back to your application.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            <Code>prompt</Code> - Defines what the login process will look like for the user. <Code>select_account</Code>
            {' '}is recommended to allow users to verify which account they will be signing in as and allow them to switch accounts. Alternatively
            {' '}<Code>login</Code> will log users out of their current Planning Center session and force them to login again.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            <Code>nonce</Code> - A unique string that will be included
            in the ID token to prevent replay attacks. If provided, your application should
            verify this value matches what was originally sent.
          </Text>
        </List.Item>
      </List>


      <Heading anchored id="oidc-workflow">
        OIDC Workflow:
      </Heading>


      <List ordered>
        <List.Item>
          <Text> Follow steps 1-3 from the "OAuth 2.0" section above to
            initiate the authorization flow, but include the <Code>openid</Code> scope and the additional recommended OIDC parameters.</Text>

          <Text>
            Redirect the user’s browser to{' '}
            <Code>
              https://api.planningcenteronline.com/oauth/authorize?client_id=CLIENT_ID&redirect_uri=https://example.com/auth/complete&response_type=code&scope=openid&state=RANDOM_STATE_VALUE&nonce=RANDOM_NONCE_VALUE&prompt=select_account
            </Code>{' '}
            (replace <Code>CLIENT_ID</Code>, <Code>RANDOM_NONCE_VALUE</Code>, <Code>RANDOM_STATE_VALUE</Code> and{' '}
            <Code>https://example.com/auth/complete</Code> with your actual application’s
            values).
          </Text>

          <Text>
            <em>If you need additional scopes, add them to <Code>scope=openid</Code>{' '}
            appropriately (see the "Application Scopes" section above).</em>
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            After exchanging the authorization code for tokens, you'll receive both an{' '}
            <Code>access_token</Code> and an <Code>id_token</Code>. The{' '}
            <Code>id_token</Code> is a JWT that contains basic user and organization
            information for the currently logged in user:
          </Text>
          <CodeBlock language="json">
            {JSON.stringify(
              {
                "iss": "https://api.planningcenteronline.com",
                "sub": "1234567890",
                "aud": "<your client id>",
                "name": "John Doe",
                "email": "john.doe@planningcenter.com",
                "organization_id": 123456,
                "organization_name": "Test Church",
                "nonce": "<your originally passed nonce value>",
                "exp": 1755801744,
                "iat": 1755798144,
              },
              null,
              2
            )}
          </CodeBlock>
        </List.Item>

        <List.Item>
          <Text>
            Refetch user data: To get the most up-to-date current user
            information, make a request to the <Code>/oauth/userinfo</Code> endpoint:
          </Text>
          <CodeBlock language="bash">
            {
              "curl -H 'Authorization: Bearer ACCESS_TOKEN' https://api.planningcenteronline.com/oauth/userinfo"
            }
          </CodeBlock>
          <Text>This will return the same users identity claims as the <Code>id_token</Code>:</Text>
          <CodeBlock language="json">
            {JSON.stringify(
              {
                "sub": "1234567890",
                "name": "John Doe",
                "email": "john.doe@planningcenter.com",
                "organization_id": 123456,
                "organization_name": "Test Church",
              },
              null,
              2
            )}
          </CodeBlock>
        </List.Item>
      </List>


      <Heading anchored id="oauth-oidc-sample-apps" size={1}>
        Sample OAuth and OIDC Applications
      </Heading>

      <Text>
        We have some example applications showing how to obtain and use access tokens
        as well as refresh tokens. They also demonstrate how to use OIDC to
        authenticate users and retrieve their information.
      </Text>
      <List>
        <List.Item>
          <Link
            to="https://github.com/planningcenter/pco_api_oauth_example_flask_python"
            external
          >
            Flask + Python
          </Link>
        </List.Item>
        <List.Item>
          <Link
            to="https://github.com/planningcenter/pco_api_oauth_example_sinatra_ruby"
            external
          >
            Sinatra + Ruby
          </Link>
        </List.Item>
        <List.Item>
          <Link
            to="https://github.com/planningcenter/pco_api_oauth_example_slim_php"
            external
          >
            Slim + PHP
          </Link>
        </List.Item>
      </List>

      <Alert type="success" marginBottom={2}>
        <Text size={4}>
          <strong>NOTE:</strong> For OIDC it is highly recommended to use a{' '}
          <Link to="https://openid.net/developers/certified-openid-connect-implementations/" external>client library for your language</Link>{' '}
          that can handle the complexities of the protocol. Some examples are <Code>oidc-client</Code>
          for Python, <Code>php-openid-client</Code> for PHP, and <Code>openid_connect</Code> for Ruby.
        </Text>
      </Alert>


      <Heading anchored id="specifying-user-agent" size={1}>
        Specifying a <Code>User-Agent</Code> header
      </Heading>

      <Text>
        All API requests must include a <Code>User-Agent</Code> header. We
        recommend using a value that uniquely identifies your
        application&mdash;typically your application&apos;s name plus a link to
        your application or your email address. For example:
      </Text>

      <List>
        <List.Item>
          <Code>
            My Planning Center App (https://pcapp.example.com/contact)
          </Code>
        </List.Item>
        <List.Item>
          <Code>PCO Demo (me@pcodemo.example.com)</Code>
        </List.Item>
      </List>

      <Text>
        <b>Note:</b> If you don&apos;t supply a <Code>User-Agent</Code> header
        that meets these requirements, your requests may result in a{' '}
        <Code>403 Forbidden</Code> response.
      </Text>
    </Fragment>
  )
}

const page = () => {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>Authentication</PageHeading>
      <Text>
        Every request to the Planning Center API is made on behalf of a user.
        Permissions are dependent on the access of this user. This also means
        that when the access a user is granted has changed, their permission in
        the API will change as well.
      </Text>

      <Text>
        There are two ways to authenticate with the API:{' '}
        <strong>Personal Access Token</strong> and <strong>OAuth</strong>.
      </Text>

      <Text>
        Personal Access Token and OAuth secrets should never be posted publicly.
        If you need to revoke your tokens, visit{' '}
        <Link
          to="https://api.planningcenteronline.com/oauth/applications"
          external
        >
          your developer account
        </Link>{' '}
        and delete the impacted tokens. If at any time they are found to be
        publicly available they will be disabled automatically.
      </Text>
      <Token />
      <OAuth />
    </StackView>
  )
}

export default page
